<template>
  <div v-if="visible" class="bo-tour" @click.self="next">
    <!-- Four panels rather than one box with a hole: a real cut-out needs canvas or an SVG mask, and
         four dimmed rectangles around the target give the same result with plain CSS and no repaint
         cost. The gap between them IS the spotlight. -->
    <div class="bo-tour__scrim" :style="scrimTop"></div>
    <div class="bo-tour__scrim" :style="scrimBottom"></div>
    <div class="bo-tour__scrim" :style="scrimLeft"></div>
    <div class="bo-tour__scrim" :style="scrimRight"></div>
    <div v-if="hasTarget" class="bo-tour__ring" :style="ringStyle"></div>

    <div class="bo-tour__card" :style="cardStyle" @click.stop>
      <div class="bo-tour__counter">Steg {{ index + 1 }} av {{ steps.length }}</div>
      <h3 class="bo-tour__title">{{ current.title }}</h3>
      <p class="bo-tour__body">{{ current.body }}</p>
      <div class="bo-tour__actions">
        <button v-if="index > 0" type="button" class="bo-tour__ghost" @click="back">Tilbake</button>
        <span class="bo-tour__spacer"></span>
        <button v-if="!isLast" type="button" class="bo-tour__ghost" @click="finish">Hopp over</button>
        <button type="button" class="bo_btn" @click="next">{{ isLast ? 'Ferdig' : 'Neste' }}</button>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * The guided tour of Backoffice — the web half of the same idea as the walkthrough in TastyPOS.
 *
 * <p>It points at the TOP-LEVEL navigation and says what lives behind each entry, rather than opening
 * dropdowns and touring individual pages. Two reasons. A new merchant's first problem is not how a screen
 * works but where the screen is, and this app has 134 routes — a tour of them would be a worse manual.
 * And driving Bootstrap dropdowns open mid-tour is fragile in a way that fails silently on exactly the
 * screen sizes nobody tests.</p>
 *
 * <p>Steps whose target is not in the DOM are skipped, which is not an edge case here: the menu is built
 * from the signed-in user's role and the merchant's enabled features, so an EMPLOYEE or a merchant
 * without loyalty simply has fewer entries. The tour has to describe the Backoffice in front of THIS
 * person.</p>
 */
import TrainingSteps from '@/util/TrainingSteps';

const SEEN_KEY = 'bo_training_seen';

export default {
  name: 'TrainingTour',
  data() {
    return {
      visible: false,
      index: 0,
      steps: [],
      rect: null,
      viewport: { w: 0, h: 0 }
    };
  },
  computed: {
    current() {
      return this.steps[this.index] || { title: '', body: '' };
    },
    isLast() {
      return this.index === this.steps.length - 1;
    },
    hasTarget() {
      return !!this.rect;
    },
    pad() {
      return 6;
    },
    box() {
      if (!this.rect) return null;
      return {
        top: this.rect.top - this.pad,
        left: this.rect.left - this.pad,
        width: this.rect.width + this.pad * 2,
        height: this.rect.height + this.pad * 2
      };
    },
    scrimTop() {
      if (!this.box) return { inset: '0' };
      return { top: 0, left: 0, right: 0, height: `${Math.max(0, this.box.top)}px` };
    },
    scrimBottom() {
      if (!this.box) return { display: 'none' };
      return { top: `${this.box.top + this.box.height}px`, left: 0, right: 0, bottom: 0 };
    },
    scrimLeft() {
      if (!this.box) return { display: 'none' };
      return { top: `${this.box.top}px`, left: 0, width: `${Math.max(0, this.box.left)}px`,
        height: `${this.box.height}px` };
    },
    scrimRight() {
      if (!this.box) return { display: 'none' };
      return { top: `${this.box.top}px`, left: `${this.box.left + this.box.width}px`, right: 0,
        height: `${this.box.height}px` };
    },
    ringStyle() {
      if (!this.box) return {};
      return { top: `${this.box.top}px`, left: `${this.box.left}px`,
        width: `${this.box.width}px`, height: `${this.box.height}px` };
    },
    cardStyle() {
      const w = 380;
      if (!this.box) {
        return { width: `${w}px`, top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
      }
      // Below the highlight when it fits, above when it does not. Clamped to the viewport so the card is
      // never half off the edge on a laptop screen with the menu near the top right.
      const below = this.box.top + this.box.height + 14;
      const fitsBelow = below + 220 < this.viewport.h;
      const top = fitsBelow ? below : Math.max(12, this.box.top - 240);
      const left = Math.min(Math.max(12, this.box.left + this.box.width / 2 - w / 2),
        this.viewport.w - w - 12);
      return { width: `${w}px`, top: `${top}px`, left: `${left}px` };
    }
  },
  mounted() {
    window.addEventListener('resize', this.measure);
    window.addEventListener('keydown', this.onKey);
  },
  unmounted() {
    window.removeEventListener('resize', this.measure);
    window.removeEventListener('keydown', this.onKey);
  },
  methods: {
    /** Runs once per browser, the first time somebody reaches a page with the tour mounted. */
    startIfFirstRun() {
      if (localStorage.getItem(SEEN_KEY) === 'true') return;
      this.start();
    },
    start() {
      // Resolved once, before the first card. Same reason as in the POS: going back becomes an ordinary
      // index-- instead of having to skip backwards past steps that are not in this menu, and the counter
      // describes what will actually be shown rather than numbering a list with holes in it.
      const all = TrainingSteps.all();
      const dropped = [];
      this.steps = all.filter((s) => {
        if (!s.target || document.querySelector(s.target)) return true;
        dropped.push(s.title);
        return false;
      });
      // Says what this role does not see. The menu is assembled from the signed-in user's role and the
      // merchant's enabled modules, so a shorter tour is usually correct — but a tour that quietly shows
      // 7 of 11 looks exactly like a tour of 7, and the only way to tell them apart is to be told.
      if (dropped.length) {
        console.log('[Opplæring] utelatt for denne rollen:', dropped.join(', '));
      }
      console.log(`[Opplæring] viser ${this.steps.length} av ${all.length} steg`);
      if (!this.steps.length) return;
      this.index = 0;
      this.visible = true;
      this.$nextTick(this.measure);
    },
    measure() {
      this.viewport = { w: window.innerWidth, h: window.innerHeight };
      const sel = this.current.target;
      if (!sel) {
        this.rect = null;
        return;
      }
      const el = document.querySelector(sel);
      if (!el) {
        this.rect = null;
        return;
      }
      el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      const r = el.getBoundingClientRect();
      this.rect = { top: r.top, left: r.left, width: r.width, height: r.height };
    },
    next() {
      if (this.isLast) {
        this.finish();
        return;
      }
      this.index += 1;
      this.$nextTick(this.measure);
    },
    back() {
      if (this.index > 0) this.index -= 1;
      this.$nextTick(this.measure);
    },
    onKey(e) {
      if (!this.visible) return;
      if (e.key === 'Escape') this.finish();
      if (e.key === 'ArrowRight') this.next();
      if (e.key === 'ArrowLeft') this.back();
    },
    finish() {
      this.visible = false;
      localStorage.setItem(SEEN_KEY, 'true');
    }
  }
};
</script>

<style scoped>
.bo-tour {
  position: fixed;
  inset: 0;
  z-index: 3000;   /* above the fixed navbar, which the tour spends most of its time pointing at */
}

.bo-tour__scrim {
  position: fixed;
  background: rgba(0, 0, 0, 0.62);
}

.bo-tour__ring {
  position: fixed;
  border: 2px solid #ECB109;
  border-radius: 6px;
  pointer-events: none;
}

.bo-tour__card {
  position: fixed;
  background: var(--bo-surface, #fff);
  color: var(--bo-ink, #212529);
  border: 1px solid var(--bo-line, #dee2e6);
  border-radius: 10px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.35);
  padding: 18px 20px 14px;
}

.bo-tour__counter {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #ECB109;
}

.bo-tour__title {
  font-size: 20px;
  font-weight: 600;
  margin: 6px 0 0;
  color: var(--bo-ink, #212529);
}

.bo-tour__body {
  font-size: 15px;
  line-height: 1.55;
  margin: 8px 0 14px;
  color: var(--bo-ink-2, #495057);
}

.bo-tour__actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.bo-tour__spacer {
  flex: 1;
}

/* Borderless, so «Tilbake» and «Hopp over» never read as the primary action next to the amber button. */
.bo-tour__ghost {
  background: none;
  border: none;
  color: var(--bo-muted, #6c757d);
  font-size: 14px;
  padding: 6px 10px;
  border-radius: 6px;
  cursor: pointer;
}
.bo-tour__ghost:hover {
  background: var(--bo-sunk, #f1f3f5);
}
</style>
